import React, {useEffect, useState} from 'react';
import {AlertCircle, Book, BookOpen, Search} from 'lucide-react';
import ClassCard from './ClassCard';
import EmptyState from '../common/EmptyState';

/**
 * Component for displaying all classes
 * Updated to match the new design in Image 2
 *
 * @param {Object} props
 * @param {Array} props.enrolledClasses - Array of enrolled classes
 * @param {Array} props.availableClasses - Array of available classes
 * @param {Function} props.handleClassSelect - Function to handle class selection
 * @param {Function} props.handleEnroll - Function to handle class enrollment
 * @param {Function} props.handleDrop - Function to handle class dropping
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message if any
 */
const ClassesView = ({
                         enrolledClasses,
                         availableClasses,
                         handleClassSelect,
                         handleEnroll,
                         handleDrop,
                         loading,
                         error
                     }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [filterEnrolled, setFilterEnrolled] = useState(false);
    const [filteredEnrolled, setFilteredEnrolled] = useState([]);
    const [filteredAvailable, setFilteredAvailable] = useState([]);

    // Apply search filter
    useEffect(() => {
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            setFilteredEnrolled(
                enrolledClasses.filter(
                    c =>
                        c.name?.toLowerCase().includes(query) ||
                        c.description?.toLowerCase().includes(query) ||
                        c.teacher?.name?.toLowerCase().includes(query)
                )
            );
            setFilteredAvailable(
                availableClasses.filter(
                    c =>
                        c.name?.toLowerCase().includes(query) ||
                        c.description?.toLowerCase().includes(query) ||
                        c.teacher?.name?.toLowerCase().includes(query)
                )
            );
        } else {
            setFilteredEnrolled(enrolledClasses);
            setFilteredAvailable(availableClasses);
        }
    }, [searchQuery, enrolledClasses, availableClasses]);

    return (
        <div className="min-h-screen bg-gray-50 font-[poppins]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-[#D67C2C]">My Classes</h1>
                    <p className="mt-2 text-gray-600">
                        Browse, join, and manage your enrolled classes
                    </p>
                </div>

                {/* Search and Filters */}
                <div className="max-w-3xl mx-auto mb-10 flex items-center justify-between">
                    <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={18} className="text-gray-400"/>
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-500"
                            placeholder="Search classes by name, subject..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <button
                        className={`ml-4 py-2 px-4 rounded-md text-sm font-medium ${
                            filterEnrolled
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        onClick={() => setFilterEnrolled(!filterEnrolled)}
                    >
                        My Classes Only
                    </button>
                </div>

                {/* Error message */}
                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-red-500"/>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-16 h-16 relative">
                            <div
                                className="absolute top-0 left-0 w-full h-full border-4 border-gray-200 rounded-full"></div>
                            <div
                                className="absolute top-0 left-0 w-full h-full border-4 border-t-blue-500 rounded-full animate-spin"></div>
                        </div>
                        <p className="mt-4 text-gray-500 font-medium">Loading classes...</p>
                    </div>
                )}

                {/* Classes Content */}
                {!loading && (
                    <div className="space-y-12">
                        {/* Enrolled Classes Section */}
                        <section>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-[#D67C2C]">
                                    My Enrolled Classes
                                </h2>
                                {filteredEnrolled.length > 0 && (
                                    <span
                                        className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                                        {filteredEnrolled.length} {filteredEnrolled.length === 1 ? 'Class' : 'Classes'}
                                    </span>
                                )}
                            </div>

                            {filteredEnrolled.length === 0 ? (
                                <EmptyState
                                    icon={BookOpen}
                                    title="No enrolled classes"
                                    description="You haven't enrolled in any classes yet. Browse available classes below."
                                />
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filteredEnrolled.map((classItem) => (
                                        <ClassCard
                                            key={classItem.class_id}
                                            classItem={classItem}
                                            isEnrolled={true}
                                            handleClassSelect={handleClassSelect}
                                            handleEnroll={handleEnroll}
                                            handleDrop={handleDrop}
                                        />
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Available Classes Section (only show if not filtering for enrolled) */}
                        {!filterEnrolled && (
                            <section>
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-2xl font-bold text-[#D67C2C]">
                                        Available Classes
                                    </h2>
                                    {filteredAvailable.length > 0 && (
                                        <span
                                            className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                                            {filteredAvailable.length} {filteredAvailable.length === 1 ? 'Class' : 'Classes'}
                                        </span>
                                    )}
                                </div>

                                {filteredAvailable.length === 0 ? (
                                    <EmptyState
                                        icon={Book}
                                        title="No available classes"
                                        description="There are no additional classes available for enrollment at this time."
                                    />
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {filteredAvailable.map((classItem) => (
                                            <ClassCard
                                                key={classItem.class_id}
                                                classItem={classItem}
                                                isEnrolled={false}
                                                handleClassSelect={handleClassSelect}
                                                handleEnroll={handleEnroll}
                                                handleDrop={handleDrop}
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClassesView;